/**
 * BoundaryPanel — what ILG does NOT claim.
 *
 * Lists the explicit non-claims from the executive abstract so adopters
 * do not over-attribute scope to the discipline, and surfaces the
 * manuscript link map for deeper reading.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { ManuscriptLink } from "./ManuscriptLink.js";
import boundaryData from "../data/boundary.json" with { type: "json" };

const ACCENT = "#7080a0";

interface NonClaim {
  readonly id: string;
  readonly title: string;
  readonly elaboration: string;
}

interface ManuscriptLinkEntry {
  readonly label: string;
  readonly path: string;
}

const NON_CLAIMS: readonly NonClaim[] = boundaryData.nonClaims;
const LINKS: readonly ManuscriptLinkEntry[] = boundaryData.manuscriptLinks;

export function BoundaryPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Boundary"
      subtitle="What ILG does not claim · what to read next"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose variant="lead">
          ILG is a <strong>lifecycle discipline</strong>, not an immunity
          kernel. The static safety claim is CSK's; ILG's job is to govern the
          conditions under which that claim may continue to be asserted.
          Over-attribution of scope is itself a structural failure mode —
          surface the non-claims explicitly to neutralize it.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.Card glowColor="rgba(239,68,68,0.10)">
        <PanelSurface.ContentList
          ariaLabel="Invariant Lifecycle Governance non-claims"
          variant="warn"
          items={NON_CLAIMS.map((nc) => ({
            id: nc.id,
            title: nc.title,
            body: nc.elaboration,
            color: panelTokens.accentRed,
          }))}
        />
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Manuscript map</PanelSurface.SectionHeading>
        <div className="mt-3.5">
          <PanelSurface.ContentList
            ariaLabel="Invariant Lifecycle Governance manuscript map"
            variant="ordered"
            dense
            items={LINKS.map((link) => ({
              id: link.path,
              title: link.label,
              body: (
                <ManuscriptLink path={link.path}>{link.path}</ManuscriptLink>
              ),
              color: panelTokens.accent,
            }))}
          />
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Frozen at v1.0.0</PanelSurface.SectionHeading>
        <PanelSurface.Prose className="mt-3">
          The original frozen-bundle hashes for v1.0.0 (computed against the
          pre-reorganization layout) are preserved as immutable audit data in{" "}
          <ManuscriptLink path="quarantine/INTAKE-2026-04-28-002/artifacts/PROVENANCE.json">
            quarantine/INTAKE-2026-04-28-002/artifacts/PROVENANCE.json
          </ManuscriptLink>
          , together with the post-intake reorganization map.
        </PanelSurface.Prose>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
